// Deep economy: loans, marketing, staff, entrance fee pricing, daily upkeep,
// and construction disruption (building closes the zoo for the rest of the day).
import { store, pushNews } from '../core/store.js';
import { bus } from '../core/bus.js';
import { AMENITIES } from './amenityCatalog.js';
import { SPECIES } from './animalCatalog.js';

// Building categories for zoo-wide, per-type service management.
const CATS = [
  ['food', '🍔', 'Food', ['food', 'pizza', 'candy']],
  ['drink', '🥤', 'Drinks', ['drink', 'coffee', 'icecream']],
  ['fun', '🎡', 'Fun', ['info', 'balloon', 'shop', 'playground', 'museum']],
  ['facility', '🚻', 'Facilities', ['toilet', 'restroom', 'vet']],
];
const CAT_OF = {};
CATS.forEach(([id, , , types]) => types.forEach((t) => { CAT_OF[t] = id; }));

export const LOAN_OPTIONS = [
  { amount: 25000, dailyInterest: 0.0009 },
  { amount: 50000, dailyInterest: 0.0011 },
  { amount: 100000, dailyInterest: 0.0014 },
];

export const MARKETING = {
  flyers:   { label: 'Flyer Campaign', price: 2500,  boost: 0.25, days: 10 },
  radio:    { label: 'Radio Ads',      price: 8000,  boost: 0.5,  days: 15 },
  tv:       { label: 'TV Commercial',  price: 25000, boost: 1.0,  days: 20 },
};

// Grand projects: one-time landmark investments — a late-game MONEY SINK that converts surplus cash
// into lasting prestige + reputation, so money stays meaningful past the first fortune.
export const PROJECTS = {
  conservation: { label: 'Conservation Fund', icon: '\uD83C\uDF3F', cost: 250000,  prestige: 20, rep: 5, desc: 'Endow a global conservation programme.' },
  research:     { label: 'Research Wing',     icon: '\uD83D\uDD2C', cost: 500000,  prestige: 35, rep: 4, desc: 'A permanent science endowment.' },
  monument:     { label: 'Grand Monument',    icon: '\uD83D\uDDFF', cost: 1000000, prestige: 60, rep: 8, desc: 'A landmark that draws the world to Arkadia.' },
};

export const STAFF = {
  keeper:  { label: 'Zookeeper',   wage: 120, effect: 'Animal wellbeing — a handful of keepers care for the whole zoo' },
  cleaner: { label: 'Cleaner',     wage: 80,  effect: 'Keeps the zoo clean — happier guests' },
  guide:   { label: 'Tour Guide',  wage: 100, effect: 'Leads tours — nearby guests gain happiness' },
};

// Government animal permit — total ADULT cap, SCALED TO THE NUMBER OF SPECIES, raised in 5 tiers.
// Only adults count. cap = speciesCount × factor → a shared pool of ANY species, so a total cap
// thins each species as you diversify (the breeding-program choice). Auto-scales if species are added.
const PERMIT_FACTORS  = [2, 4, 8, 16, 32];
const PERMIT_FEES     = [0, 300, 900, 2000, 4500];
const PERMIT_UPGRADES = [20000, 75000, 200000, 500000, null];
const SPECIES_COUNT = Object.keys(SPECIES).length;
export const PERMIT_TIERS = PERMIT_FACTORS.map((f, i) => ({
  cap: SPECIES_COUNT * f, fee: PERMIT_FEES[i], upgrade: PERMIT_UPGRADES[i],
}));
// Cap implied by a permit tier (1-based), clamped to the available tiers.
export function capForTier(tier) {
  const t = Math.min(Math.max(tier || 1, 1), PERMIT_TIERS.length);
  return PERMIT_TIERS[t - 1].cap;
}

export class EconomySystem {
  constructor(world) {
    this.world = world;
    this.marketingDaysLeft = 0;
    this.marketingBoost = 0;
    this.campaigns = [];
    this.staff = { keeper: 0, cleaner: 0, guide: 0 };
    this.closedToday = false;
    // Daily ledger accumulators (reset each new day) + zoo-wide quality cache.
    this.today = { admission: 0, shops: 0, contracts: 0, wages: 0, upkeep: 0, interest: 0, permit: 0, grants: 0, fines: 0 };
    this.financePrev = { admission: 0, shops: 0, contracts: 0, wages: 0, upkeep: 0, interest: 0, permit: 0, grants: 0, fines: 0 };
    this._quality = 1;
    // Sync the ADULT cap to the current permit tier (new games default to Tier 1; also migrates old
    // saves onto the dynamic species-scaled tiers). applySave() re-derives again after a load.
    { const pt = Math.min(Math.max(store.get().permitTier || 1, 1), PERMIT_TIERS.length); store.set({ permitTier: pt, animalCap: capForTier(pt) }); }

    this._subs = [
      bus.on('economy:takeLoan', (opt) => this.takeLoan(opt)),
      bus.on('economy:repayLoan', (amount) => this.repayLoan(amount)),
      bus.on('economy:marketing', (key) => this.startMarketing(key)),
      bus.on('economy:hire', ({ role, delta }) => this.hire(role, delta)),
      bus.on('economy:setFee', (fee) => store.set({ entranceFee: fee })),
      bus.on('permit:upgrade', () => this.upgradePermit()),
      bus.on('project:buy', (id) => {
        const def = PROJECTS[id]; if (!def) return;
        const s = store.get(); const owned = { ...(s.projects || {}) };
        if (owned[id]) return;
        if (s.money < def.cost) { bus.emit('ui:toast', { text: 'Not enough cash for this project!', kind: 'error' }); return; }
        owned[id] = true;
        store.set({ money: Math.round(s.money - def.cost), prestige: (s.prestige || 0) + def.prestige, reputation: Math.min(100, (s.reputation || 50) + def.rep), projects: owned });
        pushNews(`${def.icon} ${def.label} funded \u2014 +${def.prestige} prestige, +${def.rep} reputation!`, 'success');
        bus.emit('ui:celebrate', { kind: 'mission' });
      }),
      bus.on('sim:newDay', () => this.dailyTick()),
      // Construction disruption: placing buildings/fences closes the zoo for the day.
      bus.on('build:amenityPlace', () => { this.constructionDisruption(); this._publishCats(); }),
      // Ledger: categorise income at its source (money is added at the source; we only tally).
      bus.on('ui:sale', ({ amount }) => { this.today.shops += amount || 0; this._publishFinance(); }),
      bus.on('visitor:entered', () => { this.today.admission += (store.get().entranceFee ?? 25); this._publishFinance(); }),
      bus.on('economy:track', ({ cat, amt }) => { if (cat in this.today) { this.today[cat] += amt; this._publishFinance(); } }),
      bus.on('economy:catStaff', ({ cat, delta }) => { const c = { ...(store.get().catStaff || {}) }; c[cat] = Math.max(0, (c[cat] || 0) + delta); store.set({ catStaff: c }); this._publishCats(); }),
      bus.on('economy:catTier', ({ cat, tier }) => { const c = { ...(store.get().catTier || {}) }; c[cat] = Math.max(0, Math.min(2, tier)); store.set({ catTier: c }); this._publishCats(); }),
      bus.on('economy:catClearOverrides', (cat) => { const items = (this.world && this.world.amenities && this.world.amenities.items) || []; for (const it of items) if (CAT_OF[it.type] === cat) { it.tier = null; it.staff = null; } this._publishCats(); }),
      bus.on('economy:catApplyFrom', ({ type, tier, staff }) => { const cat = CAT_OF[type]; if (!cat) return; const c = { ...(store.get().catTier || {}) }; if (tier != null) c[cat] = Math.max(0, Math.min(2, tier)); const cs = { ...(store.get().catStaff || {}) }; if (staff != null) cs[cat] = Math.max(0, staff); const items = (this.world && this.world.amenities && this.world.amenities.items) || []; for (const it of items) if (CAT_OF[it.type] === cat) { it.tier = null; it.staff = null; } store.set({ catTier: c, catStaff: cs }); this._publishCats(); }),
    ];
    this._publishCats();
  }
  dispose() { this._subs.forEach((u) => u()); }

  constructionDisruption() {
    if (this.closedToday) return;
    this.closedToday = true;
    store.set({ zooClosed: true });
    pushNews('Construction work — the zoo is closed for the rest of the day.', 'info');
  }

  takeLoan(opt) {
    const s = store.get();
    store.set({ money: s.money + opt.amount, loan: s.loan + opt.amount, loanInterest: opt.dailyInterest });
    pushNews(`Took a $${opt.amount.toLocaleString('en-US')} bank loan.`, 'info');
  }

  repayLoan(amount) {
    const s = store.get();
    const pay = Math.min(amount, s.loan, s.money);
    if (pay <= 0) { bus.emit('ui:toast', { text: 'Cannot repay right now.', kind: 'error' }); return; }
    store.set({ money: s.money - pay, loan: s.loan - pay });
    if (store.get().loan === 0) pushNews('Loan fully repaid — the bank sends its regards.', 'success');
  }

  startMarketing(key) {
    const c = MARKETING[key];
    const s = store.get();
    if (s.money < c.price) { bus.emit('ui:toast', { text: 'Not enough cash!', kind: 'error' }); return; }
    store.set({ money: s.money - c.price });
    // Campaigns STACK: each runs on its own timer and their boosts add up.
    if (!this.campaigns) this.campaigns = [];
    this.campaigns.push({ boost: c.boost, daysLeft: c.days, label: c.label });
    this._recalcMarketing();
    pushNews(`${c.label} launched — expect more visitors!`, 'success');
  }

  _recalcMarketing() {
    this.campaigns = (this.campaigns || []).filter((m) => m.daysLeft > 0);
    this.marketingBoost = this.campaigns.reduce((sum, m) => sum + m.boost, 0);
    this.marketingDaysLeft = this.campaigns.reduce((mx, m) => Math.max(mx, m.daysLeft), 0);
  }

  hire(role, delta) {
    const next = Math.max(0, this.staff[role] + delta);
    this.staff[role] = next;
    store.set({ ['staff_' + role]: next });
    this._computeQuality();
  }

  // Pay to raise the government animal permit one tier (bigger total ADULT cap).
  upgradePermit() {
    const s = store.get();
    const tier = s.permitTier || 1;
    const cur = PERMIT_TIERS[tier - 1];
    if (!cur || cur.upgrade == null) { bus.emit('ui:toast', { text: 'Permit already at the maximum tier.', kind: 'error' }); return; }
    if (s.money < cur.upgrade) { bus.emit('ui:toast', { text: `Need $${cur.upgrade.toLocaleString('en-US')} for the permit upgrade.`, kind: 'error' }); return; }
    const nextTier = tier + 1;
    const next = PERMIT_TIERS[nextTier - 1];
    store.set({ money: s.money - cur.upgrade, permitTier: nextTier, animalCap: next.cap });
    pushNews(`Government animal permit upgraded to Tier ${nextTier} — you may now keep ${next.cap} adult animals.`, 'success');
    bus.emit('ui:toast', { text: `🏛️ Permit Tier ${nextTier} · cap ${next.cap} adults`, kind: 'success' });
    bus.emit('mandate:recheck');
  }

  // Multiplier consumed by the visitor system
  appealMultiplier() {
    const closed = store.get().zooClosed ? 0 : 1;
    return closed * (1 + this.marketingBoost);
  }

  keeperBonus(animalCount) {
    return Math.round(Math.min(1, this.staff.keeper / this._staffNeed('keeper', animalCount)) * 30);
  }

  // Realistic staff needs: sub-linear (logarithmic) so even a huge zoo needs a sane crew (~10-20),
  // not thousands. Used for cleanliness, keeper wellbeing, the "what's missing" card and auto-hire.
  _staffNeed(kind, animalCount) {
    const visitors = store.get().visitorCount || 0;
    if (kind === 'keeper') { const a = animalCount != null ? animalCount : ((this.world && this.world.animals && this.world.animals.animals.length) || 0); return Math.max(1, Math.round(2 + 4 * Math.log10(1 + a / 4))); }
    if (kind === 'cleaner') return Math.max(1, Math.round(1 + 5 * Math.log10(1 + visitors / 60)));
    if (kind === 'guide') return Math.max(1, Math.round(1 + 3 * Math.log10(1 + visitors / 80)));
    return 1;
  }

  cleanlinessScore() {
    const visitors = store.get().visitorCount || 0;
    if (visitors === 0) return 1;
    return Math.min(1, this.staff.cleaner / this._staffNeed('cleaner'));
  }

  // Total daily upkeep of placed buildings (~2.5% of build price each) — a zoo-wide fixed cost.
  _upkeepTotal() {
    const items = (this.world && this.world.amenities && this.world.amenities.items) || [];
    let sum = 0;
    for (const it of items) { const def = AMENITIES[it.type]; if (def) sum += Math.round(def.price * 0.025); }
    return sum;
  }

  // ONE zoo-wide service quality (0..1). Now driven mostly by what the player MANAGES — each building's
  // service tier (Budget/Standard/Premium) and its staffing coverage — plus cleanliness and having enough
  // outlets for the crowd. Drives BOTH shop income and visitor happiness (→ reputation → attendance).
  _computeQuality() {
    const s = store.get();
    const visitors = s.visitorCount || 0;
    const clean = this.cleanlinessScore();
    const items = ((this.world && this.world.amenities && this.world.amenities.items) || []).filter((i) => i.type !== 'entry');
    // Management quality: average of (tier + staffing coverage) across every serving building.
    let mq = 0, mn = 0;
    for (const it of items) {
      const cat = CAT_OF[it.type]; if (!cat) continue;
      const tierScore = [0.55, 0.78, 1][this.buildingTier(it)] ?? 0.78;
      const cov = Math.min(1, this.buildingCoverage(it));
      mq += 0.5 * tierScore + 0.5 * cov; mn++;
    }
    const managementQ = mn ? mq / mn : 1;
    // Enough outlets for the crowd (buildings are large, so each serves ~4× — SERVICE_SCALE).
    const amenityCov = this.needCoverage().avg;   // "shops for the crowd" = reachable need coverage (mirrors the heatmaps)
    this._quality = Math.max(0, Math.min(1, 0.5 * managementQ + 0.25 * clean + 0.25 * amenityCov));
    return this._quality;
  }
  serviceQuality() { return this._quality == null ? 1 : this._quality; }
  qualityIncomeMult() { return 0.7 + 0.5 * this.serviceQuality(); }

  // Zoo-wide guest-need coverage: fraction of PATH cells within reach (Chebyshev ≤ R) of an OPEN
  // building serving each need. Footprint-aware (uses amenities.byCell). Cached ~1.5s (per-cell scan).
  needCoverage() {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (this._covCache && now - this._covT < 1500) return this._covCache;
    const w = this.world, R = 12;
    const src = { hunger: [], thirst: [], toilet: [], fun: [] };
    if (w && w.amenities) for (const [k, it] of w.amenities.byCell) { if (it.closed) continue; const sv = (AMENITIES[it.type] || {}).serves; if (src[sv]) { const c = k.indexOf(','); src[sv].push([+k.slice(0, c), +k.slice(c + 1)]); } }
    const paths = (w && w.build) ? [...w.build.paths] : [];
    const res = { hunger: 0, thirst: 0, toilet: 0, fun: 0 };
    if (paths.length) {
      for (const sv of ['hunger', 'thirst', 'toilet', 'fun']) {
        const arr = src[sv]; if (!arr.length) { res[sv] = 0; continue; }
        let served = 0;
        for (const key of paths) { const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1); let d = 1e9; for (let j = 0; j < arr.length; j++) { const dd = Math.max(Math.abs(arr[j][0] - cx), Math.abs(arr[j][1] - cz)); if (dd < d) { d = dd; if (d === 0) break; } } if (d <= R) served++; }
        res[sv] = served / paths.length;
      }
    }
    res.avg = (res.hunger + res.thirst + res.toilet + res.fun) / 4;
    this._covCache = res; this._covT = now;
    return res;
  }

  // Zoo-wide COMFORT coverage: fraction of path cells within reach of a bin / lamp / bench.
  // Generous reaches (bin 15, bench 15, lamp 12) so a handful of props cover the network. Cached ~1.5s.
  comfortCoverage() {
    const now = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    if (this._comCache && now - this._comT < 1500) return this._comCache;
    const w = this.world;
    const specs = [['bin', 15], ['lamp', 12], ['bench', 15]];
    const src = { bin: [], lamp: [], bench: [] };
    if (w && w.props) for (const it of w.props.items) { if (src[it.type]) src[it.type].push([it.cx, it.cz]); }
    const paths = (w && w.build) ? [...w.build.paths] : [];
    const res = { bin: 0, lamp: 0, bench: 0 };
    if (paths.length) {
      for (const [ty, R] of specs) {
        const arr = src[ty]; if (!arr.length) { res[ty] = 0; continue; }
        let served = 0;
        for (const key of paths) { const c = key.indexOf(','), cx = +key.slice(0, c), cz = +key.slice(c + 1); let d = 1e9; for (let j = 0; j < arr.length; j++) { const dd = Math.max(Math.abs(arr[j][0] - cx), Math.abs(arr[j][1] - cz)); if (dd < d) { d = dd; if (d === 0) break; } } if (d <= R) served++; }
        res[ty] = served / paths.length;
      }
    }
    res.avg = (res.bin + res.lamp + res.bench) / 3;
    this._comCache = res; this._comT = now;
    return res;
  }

  // Full "what's missing" breakdown: the weighted service-quality parts + staff have/need + comfort &
  // need coverage. Lets the player see exactly which factor is dragging the score down.
  qualityBreakdown() {
    const s = store.get();
    const visitors = s.visitorCount || 0;
    const animals = (this.world && this.world.animals && this.world.animals.animals.length) || 0;
    const items = ((this.world && this.world.amenities && this.world.amenities.items) || []).filter((i) => i.type !== 'entry');
    let mq = 0, mn = 0;
    for (const it of items) { const cat = CAT_OF[it.type]; if (!cat) continue; const ts = [0.55, 0.78, 1][this.buildingTier(it)] ?? 0.78; const cov = Math.min(1, this.buildingCoverage(it)); mq += 0.5 * ts + 0.5 * cov; mn++; }
    const management = mn ? mq / mn : 1;
    const clean = this.cleanlinessScore();
    const need = this.needCoverage(), com = this.comfortCoverage();
    const amenityCov = need.avg;   // reachable food/drinks/toilets/fun — same as the heatmaps show
    return {
      quality: this.serviceQuality(),
      quality_parts: [
        { label: 'Service tiers & staffing', val: management, w: 50 },
        { label: 'Cleanliness', val: clean, w: 25 },
        { label: 'Shops within reach', val: amenityCov, w: 25 },
      ],
      staff: [
        { label: 'Zookeepers', have: this.staff.keeper, need: this._staffNeed('keeper', animals), note: 'animal wellbeing' },
        { label: 'Cleaners', have: this.staff.cleaner, need: this._staffNeed('cleaner'), note: 'cleanliness' },
        { label: 'Tour guides', have: this.staff.guide, need: this._staffNeed('guide'), note: 'guest happiness' },
      ],
      comfort: [
        { label: 'Seating', val: com.bench }, { label: 'Bins', val: com.bin }, { label: 'Lighting', val: com.lamp },
      ],
      needs: [
        { label: 'Food', val: need.hunger }, { label: 'Drinks', val: need.thirst }, { label: 'Toilets', val: need.toilet }, { label: 'Fun', val: need.fun },
      ],
    };
  }

  // --- Per-category service (staffing coverage + Budget/Standard/Premium tier) ---
  _catBuildings(cat) { const items = (this.world && this.world.amenities && this.world.amenities.items) || []; let n = 0; for (const it of items) if (CAT_OF[it.type] === cat) n++; return n; }
  // Buildings still on the category pool (no per-building staff override) — the pool only covers THESE.
  _catAutoBuildings(cat) { const items = (this.world && this.world.amenities && this.world.amenities.items) || []; let n = 0; for (const it of items) if (CAT_OF[it.type] === cat && it.staff == null) n++; return n; }
  _catCustom(cat) { const items = (this.world && this.world.amenities && this.world.amenities.items) || []; let n = 0; for (const it of items) if (CAT_OF[it.type] === cat && (it.staff != null || it.tier != null)) n++; return n; }
  _tier(cat) { const t = (store.get().catTier || {})[cat]; return t == null ? 1 : t; }
  categoryCoverage(cat) { const b = this._catAutoBuildings(cat); const st = (store.get().catStaff || {})[cat] || 0; return b > 0 ? Math.min(1, st * 4 / b) : 1; }
  categoryIncomeMult(type) { const cat = CAT_OF[type]; if (!cat) return 1; const cov = this.categoryCoverage(cat); const tm = [0.85, 1.0, 1.25][this._tier(cat)]; return tm * (0.7 + 0.3 * cov); }
  categoryHappyMult(type) { const cat = CAT_OF[type]; if (!cat) return 1; const cov = this.categoryCoverage(cat); const tm = [0.85, 1.0, 1.2][this._tier(cat)]; return tm * (0.6 + 0.4 * cov); }
  categoryQualityPct(cat) { const cov = this.categoryCoverage(cat); const t = this._tier(cat) / 2; return Math.round(Math.max(0, Math.min(1, 0.5 * cov + 0.5 * t)) * 100); }
  // Per-BUILDING service: an item may override its category's tier (Budget/Standard/Premium).
  buildingTier(item) { const cat = CAT_OF[item.type]; return (item && item.tier != null) ? item.tier : this._tier(cat); }
  // Per-building staff coverage: an item may deviate from the category pool with dedicated staff.
  buildingCoverage(item) { const cat = CAT_OF[item.type]; if (item && item.staff != null) return Math.min(1.15, 0.3 + item.staff * 0.38); return this.categoryCoverage(cat); }
  buildingIncomeMult(item) { const cat = CAT_OF[item.type]; if (!cat) return 1; const cov = this.buildingCoverage(item); const tm = [0.85, 1.0, 1.25][this.buildingTier(item)] ?? 1; return tm * (0.7 + 0.3 * cov); }
  buildingHappyMult(item) { const cat = CAT_OF[item.type]; if (!cat) return 1; const cov = this.buildingCoverage(item); const tm = [0.85, 1.0, 1.2][this.buildingTier(item)] ?? 1; return tm * (0.6 + 0.4 * cov); }
  _publishCats() { const info = {}; for (const [id] of CATS) info[id] = { buildings: this._catBuildings(id), staff: (store.get().catStaff || {})[id] || 0, tier: this._tier(id), quality: this.categoryQualityPct(id) }; store.set({ catInfo: info }); }

  _publishFinance() { store.set({ finance: { ...this.today } }); }

  dailyTick() {
    const s = store.get();

    // Kids mode: the zoo hires its own staff when affordable — no management needed
    if (s.kidsMode && this.world?.animals) {
      const needKeepers = this._staffNeed('keeper');
      if (this.staff.keeper < needKeepers && s.money > 8000) { this.hire('keeper', 1); pushNews('Your zoo hired a zookeeper to care for the animals. 🧑‍🌾', 'info'); }
      const needCleaners = s.visitorCount > 30 ? this._staffNeed('cleaner') : 0;
      if (this.staff.cleaner < needCleaners && s.money > 8000) { this.hire('cleaner', 1); pushNews('Your zoo hired a cleaner to keep things tidy. 🧹', 'info'); }
    }

    // Close yesterday's books, then open a fresh day that starts with its fixed costs booked.
    this.financePrev = { ...this.today };
    this.today = { admission: 0, shops: 0, contracts: 0, wages: 0, upkeep: 0, interest: 0, permit: 0, grants: 0, fines: 0 };
    const interest = s.loan > 0 ? Math.ceil(s.loan * (s.loanInterest || 0.001)) : 0;
    const catStaff = store.get().catStaff || {};
    const catWages = Object.values(catStaff).reduce((a, n) => a + (n || 0), 0) * 70;
    let tierCost = 0, bldgStaff = 0; const _am = (this.world && this.world.amenities && this.world.amenities.items) || []; for (const it of _am) { if (it.type === 'entry' || !CAT_OF[it.type]) continue; tierCost += this.buildingTier(it) * 8; if (it.staff != null) bldgStaff += it.staff; }
    const wages = this.staff.keeper * STAFF.keeper.wage + this.staff.cleaner * STAFF.cleaner.wage + this.staff.guide * STAFF.guide.wage + catWages + bldgStaff * 70;
    const upkeep = this._upkeepTotal() + tierCost;
    const permitFee = (PERMIT_TIERS[(s.permitTier || 1) - 1] || {}).fee || 0;
    this.today.interest = interest; this.today.wages = wages; this.today.upkeep = upkeep; this.today.permit = permitFee;
    this._publishCats();
    const expenses = interest + wages + upkeep + permitFee;
    if (expenses > 0) store.set({ money: Math.round(store.get().money - expenses) });
    this._computeQuality();
    store.set({ finance: { ...this.today }, financePrev: { ...this.financePrev }, serviceQuality: this._quality });

    // STAR ATTRACTIONS: rare species + morphs lift reputation a touch each day (drives attendance).
    if (this.world && this.world.animals) {
      const sp = this.world.animals.starPower();
      if (sp.score > 0) store.set({ reputation: Math.min(100, (store.get().reputation || 50) + Math.min(2, sp.score * 0.04)) });
    }

    // WELFARE INSPECTION: every 30 days a government audit grades average wellbeing — grant or fine.
    this._inspDay = (this._inspDay || 0) + 1;
    if (this._inspDay >= 30 && this.world && this.world.animals && this.world.animals.animals.length) {
      this._inspDay = 0;
      const A = this.world.animals.animals.filter((a) => !a.escaped);
      const avg = A.length ? A.reduce((sum, a) => sum + (a.wellbeing || 0), 0) / A.length : 0;
      let grade, delta, cash;
      if (avg >= 80) { grade = 'A'; cash = Math.round(2000 + A.length * 40); delta = 3; }
      else if (avg >= 65) { grade = 'B'; cash = Math.round(800 + A.length * 15); delta = 1; }
      else if (avg >= 50) { grade = 'C'; cash = 0; delta = 0; }
      else if (avg >= 35) { grade = 'D'; cash = -Math.round(1000 + A.length * 15); delta = -3; }
      else { grade = 'E'; cash = -Math.round(2500 + A.length * 30); delta = -6; }
      const st = store.get();
      if (cash > 0) { this.today.grants = (this.today.grants || 0) + cash; store.set({ money: st.money + cash }); }
      else if (cash < 0) { this.today.fines = (this.today.fines || 0) + (-cash); store.set({ money: st.money + cash }); }
      store.set({ reputation: Math.max(5, Math.min(100, (store.get().reputation || 50) + delta)), finance: { ...this.today }, inspection: { grade, avg: Math.round(avg), cash, day: st.day } });
      pushNews(`\uD83C\uDFDB\uFE0F Welfare inspection: grade ${grade} (avg wellbeing ${Math.round(avg)}%). ${cash > 0 ? 'Conservation grant $' + cash.toLocaleString('en-US') + '.' : cash < 0 ? 'Fined $' + (-cash).toLocaleString('en-US') + '.' : 'No penalty.'}`, cash < 0 ? 'error' : 'success');
    }

    // Marketing countdown — tick each active campaign, drop expired ones (they stack).
    if (this.campaigns && this.campaigns.length) {
      let ended = 0;
      for (const m of this.campaigns) { m.daysLeft--; if (m.daysLeft <= 0) ended++; }
      this._recalcMarketing();
      if (ended > 0 && this.campaigns.length === 0) pushNews('Marketing campaign has ended.', 'info');
      else if (ended > 0) pushNews('A marketing campaign has ended.', 'info');
    }

    // Re-open after construction
    if (this.closedToday) {
      this.closedToday = false;
      store.set({ zooClosed: false });
      pushNews('The zoo has reopened after construction.', 'success');
    }

    // Bankruptcy pressure
    const money = store.get().money;
    if (money < 0 && !this._debtWarned) {
      this._debtWarned = true;
      pushNews('Finances in the red — take a loan or cut costs!', 'error');
    }
    if (money > 0) this._debtWarned = false;
  }
}
